/**
 * SSE frame parsing for the skyAgent chat stream (Contract B).
 *
 * Deliberately React-free and dependency-free so it can be proven in the fast
 * `*.nodb.test.ts` lane (plain Node env, no jsdom, no DB) rather than only
 * through a rendered component.
 *
 * Contract B's wire format: frames are `data: {json}\n\n`, a `: heartbeat`
 * comment frame may appear at any point, and the stream ends with
 * `data: [DONE]`.
 */
import type { ChatChunk } from "@/store/api/crmSkyAgentApi";

/**
 * Read an SSE byte stream to completion, invoking `onChunk` per parsed chunk in
 * arrival order.
 *
 * Two things this must get right, both of which a naive implementation gets
 * wrong and neither of which shows up until production:
 *   - Frame boundaries do NOT align with read boundaries. One read can carry
 *     half a frame, or three frames; the leftover is buffered across reads.
 *   - A multi-byte UTF-8 character can be split across two reads, so the
 *     decoder must be called with `{ stream: true }`.
 */
export async function consumeSseStream(
  body: ReadableStream<Uint8Array>,
  onChunk: (chunk: ChatChunk) => void,
): Promise<void> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    let sep: number;
    while ((sep = buffer.indexOf("\n\n")) !== -1) {
      const frame = buffer.slice(0, sep);
      buffer = buffer.slice(sep + 2);

      for (const line of frame.split("\n")) {
        // Skips `: heartbeat` comment frames without needing a special case.
        if (!line.startsWith("data:")) continue;
        const data = line.slice(5).trim();
        if (!data) continue;
        if (data === "[DONE]") {
          buffer = "";
          continue;
        }
        try {
          onChunk(JSON.parse(data) as ChatChunk);
        } catch {
          /* ignore a non-JSON keep-alive payload */
        }
      }
    }
  }
}
